import type { JSX } from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import {
  CheckOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DownOutlined,
  InfoCircleOutlined,
  SendOutlined,
  UpOutlined,
} from '@ant-design/icons';

import { Button, Panel, Tag } from '../ds';
import { friendlyError } from '../../lib/api';
import {
  OPENING_TASKS_KEYS,
  openingTasksApi,
  type MyOpeningTasksDto,
  type StageTaskDto,
} from '../../lib/api/openingTasks';
import { CardTaskRow } from './CardTaskRow';
import { missingHint, splitStageTasks, stageProgress } from './stage-tasks';
import { useViewAsReadOnly } from './useViewAsReadOnly';
import { fmtDate, wikiLink } from './utils';

/** Свёрнут ли блок задач этапа — выбор пользователя помнится (владелец 07.10.2026). */
const COLLAPSE_KEY = 'fk-ot-stage-tasks-collapsed';

function readCollapsed(): boolean | null {
  try {
    const v = window.localStorage.getItem(COLLAPSE_KEY);
    return v === '1' ? true : v === '0' ? false : null;
  } catch {
    return null;
  }
}

function writeCollapsed(v: boolean): void {
  try {
    window.localStorage.setItem(COLLAPSE_KEY, v ? '1' : '0');
  } catch {
    /* приватный режим — не помним */
  }
}

const KIND_LABEL: Record<StageTaskDto['kind'], string> = {
  article: 'Статья',
  course: 'Курс',
  auto: 'По заявке',
  manual: 'Действие',
};

/**
 * Задачи текущего этапа открытия. Первыми — задачи карточки по шаблону плана (владелец 19.10.2026): партнёр
 * отмечает «Сделано» и пункты чек-листа. Ниже — прежние пункты этапа: статьи/курсы/авто-пункты отмечаются сами,
 * ручные — галочкой партнёра (оптимистично). Показываем только то, что сейчас в работе; выполненное (и задачи
 * карточки, и прежние пункты) прячется за кнопку «Показать выполненные» (владелец 31.10.2026). Внизу — заявка
 * куратору на переход на следующий этап (решает куратор, недостающее — только подсказка). «Смотреть от лица» —
 * только просмотр: отметки неактивны.
 */
export function StageTasksPanel({ data }: { data: MyOpeningTasksDto }): JSX.Element {
  const readOnly = useViewAsReadOnly();
  const cardParts = splitStageTasks(data.cardTasks ?? []);
  const legacyParts = splitStageTasks(data.stageTasks);
  const doneCount = cardParts.done.length + legacyParts.done.length;
  const [showDone, setShowDone] = useState(false);
  const { done, total } = stageProgress(data);
  const pct = total ? Math.round((done / total) * 100) : 0;
  const stageLabel = data.pipeline?.stage.label ?? '';
  // По умолчанию открыт; всё выполнено — свёрнут. Ручной выбор помнится.
  const [collapsed, setCollapsed] = useState<boolean>(() => readCollapsed() ?? (total > 0 && done === total));
  const toggle = (): void => {
    setCollapsed((c) => {
      writeCollapsed(!c);
      return !c;
    });
  };

  return (
    <Panel
      pad={0}
      icon={<CheckSquareOutlined />}
      title={
        <button type="button" className="fk-ot-collapse-title" onClick={toggle} aria-expanded={!collapsed} aria-controls="fk-ot-stage-tasks">
          {stageLabel ? `Задачи этапа «${stageLabel}»` : 'Задачи этапа'}
        </button>
      }
      sub={collapsed ? undefined : (data.cardTasks ?? []).length > 0 ? 'то, что сейчас в работе по плану вашей точки' : 'статьи и действия, которые УК задала для этапа'}
      right={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {total ? <span className="fk-ot-mono fk-ot-muted">{done} из {total}</span> : null}
          <Button
            size="sm"
            variant="ghost"
            icon={collapsed ? <DownOutlined /> : <UpOutlined />}
            onClick={toggle}
            aria-expanded={!collapsed}
            aria-controls="fk-ot-stage-tasks"
            aria-label={collapsed ? 'Развернуть задачи этапа' : 'Свернуть задачи этапа'}
          />
        </span>
      }
    >
      <div id="fk-ot-stage-tasks" hidden={collapsed}>
        {total > 0 ? (
          <>
            <div className="fk-ot-progress">
              <div className="fk-ot-bar" data-done={done === total}>
                <span style={{ width: `${pct}%` }} />
              </div>
            </div>
            {cardParts.open.length > 0 ? (
              <div>
                {cardParts.open.map((t, i) => (
                  <CardTaskRow key={t.id} task={t} index={i} readOnly={readOnly} />
                ))}
              </div>
            ) : null}
            {legacyParts.open.length > 0 ? (
              <div>
                {cardParts.open.length > 0 ? <div className="fk-ot-subhead">Статьи и курсы этапа</div> : null}
                {legacyParts.open.map((t, i) => (
                  <StageTaskRow key={t.id} task={t} index={i} readOnly={readOnly} />
                ))}
              </div>
            ) : null}
            {cardParts.open.length === 0 && legacyParts.open.length === 0 ? (
              <div className="fk-ot-empty">Все задачи этапа выполнены.</div>
            ) : null}
            {doneCount > 0 ? (
              <div className="fk-ot-more">
                <Button
                  size="sm"
                  variant="text"
                  icon={showDone ? <UpOutlined /> : <DownOutlined />}
                  aria-expanded={showDone}
                  onClick={() => setShowDone((v) => !v)}
                >
                  {showDone ? 'Скрыть выполненные' : `Показать выполненные (${doneCount})`}
                </Button>
              </div>
            ) : null}
            {showDone && doneCount > 0 ? (
              <div>
                {cardParts.done.map((t, i) => (
                  <CardTaskRow key={t.id} task={t} index={i} readOnly={readOnly} />
                ))}
                {legacyParts.done.length > 0 ? (
                  <div>
                    {cardParts.done.length > 0 ? <div className="fk-ot-subhead">Статьи и курсы этапа</div> : null}
                    {legacyParts.done.map((t, i) => (
                      <StageTaskRow key={t.id} task={t} index={i} readOnly={readOnly} />
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
          </>
        ) : (
          <div className="fk-ot-empty">Для этого этапа УК пока не задала задач — следите за перепиской с куратором.</div>
        )}
        <StageRequestBlock data={data} />
      </div>
    </Panel>
  );
}

function StageTaskRow({ task, index, readOnly }: { task: StageTaskDto; index: number; readOnly: boolean }): JSX.Element {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const toggle = useMutation({
    mutationFn: (next: boolean) => openingTasksApi.toggleTask(task.id, next),
    onMutate: async (next: boolean) => {
      await qc.cancelQueries({ queryKey: OPENING_TASKS_KEYS.myTasks });
      const prev = qc.getQueryData<MyOpeningTasksDto>(OPENING_TASKS_KEYS.myTasks);
      if (prev) {
        qc.setQueryData<MyOpeningTasksDto>(OPENING_TASKS_KEYS.myTasks, {
          ...prev,
          stageTasks: prev.stageTasks.map((x) =>
            x.id === task.id ? { ...x, done: next, doneAt: next ? new Date().toISOString() : null } : x,
          ),
        });
      }
      return { prev };
    },
    onError: (e, _next, ctx) => {
      if (ctx?.prev) qc.setQueryData(OPENING_TASKS_KEYS.myTasks, ctx.prev);
      message.error(friendlyError(e));
    },
    onSettled: () => qc.invalidateQueries({ queryKey: OPENING_TASKS_KEYS.myTasks }),
  });

  const link = wikiLink(task.link);
  const title = link ? (
    link.external ? (
      <a href={link.to} target="_blank" rel="noopener noreferrer">{task.title}</a>
    ) : (
      <Link to={link.to}>{task.title}</Link>
    )
  ) : (
    task.title
  );

  const when = task.doneAt ? ` · ${fmtDate(task.doneAt)}` : '';
  let sub: string;
  if (task.canToggle) sub = task.done ? `отмечено вами${when}` : 'отметьте, когда сделаете';
  else if (task.kind === 'article') sub = task.done ? `статья прочитана${when}` : 'статья · отметится сама, когда прочитаете';
  else if (task.kind === 'course') sub = task.done ? `курс пройден${when}` : 'курс · отметится сам, когда пройдёте';
  else if (task.auto) sub = task.done ? `отмечено по заявке${when}` : 'отмечается само по заявке на осмотр';
  else sub = task.done ? `выполнено${when}` : KIND_LABEL[task.kind].toLowerCase();

  const mark = task.done ? <CheckOutlined /> : index + 1;
  return (
    <div className="fk-ot-lrow" data-done={task.done}>
      {task.canToggle ? (
        <button
          type="button"
          className="fk-ot-num"
          data-done={task.done}
          disabled={toggle.isPending || readOnly}
          title={readOnly ? 'Режим просмотра — только чтение' : undefined}
          aria-pressed={task.done}
          aria-label={`${task.done ? 'Снять отметку' : 'Отметить выполненным'}: ${task.title}`}
          onClick={() => toggle.mutate(!task.done)}
        >
          {mark}
        </button>
      ) : (
        <span className="fk-ot-num" data-done={task.done} title={task.auto ? 'Отмечается само' : undefined}>
          {mark}
        </span>
      )}
      <div style={{ minWidth: 0 }}>
        <div className="fk-ot-ltitle">{title}</div>
        <div className="fk-ot-lsub">{sub}</div>
      </div>
      {task.required ? (
        <Tag color="orange">Обязательно</Tag>
      ) : task.kind === 'auto' ? (
        <Tag color="indigo">По заявке</Tag>
      ) : (
        <Tag>{KIND_LABEL[task.kind]}</Tag>
      )}
    </div>
  );
}

/** Заявка на переход: на рассмотрении → «Отозвать»; отклонена → комментарий куратора; иначе — подать. */
function StageRequestBlock({ data }: { data: MyOpeningTasksDto }): JSX.Element | null {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [formOpen, setFormOpen] = useState(false);
  const [comment, setComment] = useState('');
  const req = data.stageRequest;
  const next = data.pipeline?.nextStage ?? null;

  const submit = useMutation({
    mutationFn: () => openingTasksApi.requestNextStage(comment.trim() || undefined),
    onSuccess: (created) => {
      qc.setQueryData<MyOpeningTasksDto>(OPENING_TASKS_KEYS.myTasks, (prev) =>
        prev ? { ...prev, stageRequest: created, canRequestNext: false } : prev,
      );
      setFormOpen(false);
      setComment('');
      message.success('Заявка отправлена куратору');
      void qc.invalidateQueries({ queryKey: OPENING_TASKS_KEYS.myTasks });
    },
    onError: (e) => message.error(friendlyError(e)),
  });
  const cancel = useMutation({
    mutationFn: () => openingTasksApi.cancelStageRequest(),
    onSuccess: () => {
      message.success('Заявка отозвана');
      void qc.invalidateQueries({ queryKey: OPENING_TASKS_KEYS.myTasks });
    },
    onError: (e) => message.error(friendlyError(e)),
  });

  if (!data.pipeline) return null;

  if (req?.status === 'pending') {
    return (
      <div className="fk-ot-req">
        <div className="fk-ot-callout" data-tone="info">
          <ClockCircleOutlined />
          <div style={{ minWidth: 0 }}>
            Заявка на переход в «<b>{req.toStageLabel}</b>» отправлена куратору {fmtDate(req.createdAt)}. Куратор проверит задачи этапа и переведёт.
            {req.comment ? <div className="fk-ot-note" style={{ marginTop: 4 }}>Ваш комментарий: {req.comment}</div> : null}
          </div>
        </div>
        <div className="fk-ot-req-row">
          <Button variant="ghost" size="sm" loading={cancel.isPending} onClick={() => cancel.mutate()}>
            Отозвать
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fk-ot-req">
      {req?.status === 'rejected' ? (
        <div className="fk-ot-callout" data-tone="error">
          <CloseCircleOutlined />
          <div style={{ minWidth: 0 }}>
            Куратор вернул заявку на переход в «<b>{req.toStageLabel}</b>»{req.decidedAt ? ` ${fmtDate(req.decidedAt)}` : ''}
            {req.decidedBy ? ` (${req.decidedBy})` : ''}.
            {req.decisionComment ? <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>«{req.decisionComment}»</div> : null}
          </div>
        </div>
      ) : null}

      {data.missingRequired.length > 0 ? (
        <div className="fk-ot-callout" data-tone="warn">
          <InfoCircleOutlined />
          <div style={{ minWidth: 0 }}>
            Ещё не выполнено обязательное: {missingHint(data.missingRequired)}. Заявку подать можно — решение принимает куратор.
          </div>
        </div>
      ) : null}

      {!next ? (
        <div className="fk-ot-note">Это последний этап открытия — дальше куратор отметит открытие точки.</div>
      ) : formOpen ? (
        <div style={{ display: 'grid', gap: 8 }}>
          <label className="fk-ot-note" htmlFor="fk-ot-req-comment">
            Комментарий куратору (необязательно)
          </label>
          <textarea
            id="fk-ot-req-comment"
            className="fk-ot-textarea"
            value={comment}
            maxLength={2000}
            placeholder="Например: договор аренды подписан, скан в переписке по помещению"
            onChange={(e) => setComment(e.target.value)}
          />
          <div className="fk-ot-req-row">
            <Button variant="primary" size="sm" icon={<SendOutlined />} loading={submit.isPending} onClick={() => submit.mutate()}>
              Отправить заявку
            </Button>
            <Button variant="text" size="sm" disabled={submit.isPending} onClick={() => setFormOpen(false)}>
              Отмена
            </Button>
          </div>
        </div>
      ) : (
        <div className="fk-ot-req-row">
          <Button variant="primary" size="sm" disabled={!data.canRequestNext} onClick={() => setFormOpen(true)}>
            Подать заявку на переход в «{next.label}»
          </Button>
          {!data.canRequestNext ? <span className="fk-ot-note">Сейчас подать заявку нельзя — вопросы по этапу задайте куратору.</span> : null}
        </div>
      )}
    </div>
  );
}
